import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  isHighlightedPopupEnabled,
  setHighlightedPopupEnabled,
} from '../../utils/highlightedNotificationPrefs';
import {
  clearHighlightedToasts,
  pushHighlightedToast,
} from '../../store/slices/userNotificationSlice';
import { formatTaskDelegatedNotification, formatPanelAccessNotification, buildMessageHighlightToast } from '../../utils/notificationMessageUtils';

const HighlightNotificationToggle = () => {
  const dispatch = useDispatch();
  const [enabled, setEnabled] = useState(() => isHighlightedPopupEnabled());
  const fabUserNotifications = useSelector((state) => state.userNotifications.fabUserNotifications || []);
  const fabMeetingNotifications = useSelector((state) => state.meetingNotifications?.fabMeetingNotifications || []);
  const fabTaskNotifications = useSelector((state) => state.tasks.fabTaskNotifications || []);
  const fabPanelNotifications = useSelector((state) => state.panelAccess.fabPanelNotifications || []);
  const mentionAlerts = useSelector((state) => state.chat.mentionAlerts || []);

  useEffect(() => {
    const sync = () => setEnabled(isHighlightedPopupEnabled());
    window.addEventListener('workloop:highlight-prefs-changed', sync);
    return () => window.removeEventListener('workloop:highlight-prefs-changed', sync);
  }, []);

  const mirrorBellItems = () => {
    fabUserNotifications.forEach((event) => dispatch(pushHighlightedToast(event)));
    fabMeetingNotifications.forEach((event) => dispatch(pushHighlightedToast({
      ...event,
      eventType: event.type || event.eventType,
      title: event.notificationTitle || event.title,
      body: event.notificationBody || event.title,
      ticketTitle: event.title,
      sourceChannelId: event.channelId,
      sourceMessageId: event.chatMessageId,
    })));
    fabTaskNotifications.forEach((event) => {
      const formatted = formatTaskDelegatedNotification(event);
      dispatch(pushHighlightedToast({
        eventType: 'TASK_ASSIGNED',
        taskId: event.task?.id,
        title: formatted.title,
        body: formatted.body,
        ticketTitle: event.task?.title,
        actorName: event.delegatorName,
      }));
    });
    fabPanelNotifications.forEach((event) => {
      const formatted = formatPanelAccessNotification(event);
      dispatch(pushHighlightedToast({
        eventType: event.type || 'PANEL_ACCESS',
        title: formatted.title,
        body: formatted.body,
        channelId: event.request?.channelId || event.request?.id,
      }));
    });
    mentionAlerts.forEach((alert) => dispatch(pushHighlightedToast(
      buildMessageHighlightToast({
        ...alert.message,
        channelId: alert.message?.channelId || alert.channelId,
      }, {
        eventType: 'MENTION',
        channelName: alert.channelName,
        isDirectMessage: alert.isDirectMessage,
      })
    )));
  };

  const toggle = () => {
    const next = !enabled;
    setHighlightedPopupEnabled(next);
    setEnabled(next);
    if (next) mirrorBellItems();
    else dispatch(clearHighlightedToasts());
  };

  const hint = enabled
    ? 'On · new bell items also appear as bottom-right popups'
    : 'Off · only show notifications in this list';

  return (
    <div className="relative group/toggle flex-shrink-0">
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label="Bottom-PopUp"
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        title={hint}
        className={`inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-left transition-colors ${
          enabled
            ? 'border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/40'
            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
        }`}
      >
        <span className="whitespace-nowrap text-[11px] font-semibold text-gray-900 dark:text-white">
          Bottom-PopUp
        </span>
        <span
          className={`relative h-4 w-7 flex-shrink-0 rounded-full transition-colors duration-200 ${
            enabled ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span
            className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform duration-200 ${
              enabled ? 'translate-x-3.5' : 'translate-x-0.5'
            }`}
          />
        </span>
      </button>
      <span className="pointer-events-none absolute left-0 bottom-full z-30 mb-1 hidden w-max max-w-[11rem] rounded-md bg-gray-900 px-2 py-1 text-[10px] leading-snug text-white shadow-lg group-hover/toggle:block">
        {hint}
      </span>
    </div>
  );
};

export default HighlightNotificationToggle;
